import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { formStyles } from '@utils/formStyles';

interface TypeaheadOption {
    value: string;
    label: string;
}

interface TypeaheadGroup {
    label: string;
    options: TypeaheadOption[];
}

interface FeatureTypeaheadProps {
    label: string;
    placeholder?: string;
    helpText?: string;
    selectedValue: string;
    selectedLabel: string | null;
    searchValue: string;
    options: TypeaheadGroup[];
    onSearchChange: (value: string) => void;
    onSelect: (value: string) => void;
}

interface FlatOption extends TypeaheadOption {
    groupLabel: string;
    flatIndex: number;
}

const buildFlatOptions = (groups: TypeaheadGroup[]): FlatOption[] => {
    const flat: FlatOption[] = [];
    let flatIndex = 0;

    for (const group of groups) {
        for (const option of group.options) {
            if (option.value === '') continue;
            flat.push({ ...option, groupLabel: group.label, flatIndex });
            flatIndex += 1;
        }
    }

    return flat;
};

const FeatureTypeahead = ({
    label,
    placeholder,
    helpText,
    selectedValue,
    selectedLabel,
    searchValue,
    options,
    onSearchChange,
    onSelect
}: FeatureTypeaheadProps) => {
    const generatedId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const flatOptions = useMemo(() => buildFlatOptions(options), [options]);
    const totalCount = flatOptions.length;

    const closeDropdown = () => {
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const effectiveHighlight = useMemo(() => {
        if (!isOpen || totalCount === 0) return -1;
        if (highlightedIndex < 0) return 0;
        if (highlightedIndex >= totalCount) return totalCount - 1;
        return highlightedIndex;
    }, [highlightedIndex, isOpen, totalCount]);

    useEffect(() => {
        if (!isOpen || effectiveHighlight < 0) return;

        const list = listRef.current;
        if (!list) return;

        const target = list.querySelector<HTMLElement>(`[data-flat-index="${effectiveHighlight}"]`);
        target?.scrollIntoView({ block: 'nearest' });
    }, [effectiveHighlight, isOpen]);

    const displayValue = isOpen ? searchValue : (selectedLabel ?? searchValue);

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!isOpen) setIsOpen(true);
            setHighlightedIndex((current) => Math.min(totalCount - 1, Math.max(current, -1) + 1));
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((current) => Math.max(0, current - 1));
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();

            if (!isOpen) {
                setIsOpen(true);
                return;
            }

            const candidate = flatOptions[effectiveHighlight];

            if (candidate) {
                onSelect(candidate.value);
                onSearchChange('');
                closeDropdown();
                inputRef.current?.blur();
            }

            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            closeDropdown();
            return;
        }

        if (event.key === 'Tab') {
            closeDropdown();
        }
    };

    const handleClear = () => {
        onSelect('');
        onSearchChange('');
        closeDropdown();
        inputRef.current?.focus();
    };

    return (
        <div className={formStyles.fullWidthContainer} ref={containerRef}>
            <label className={formStyles.label} htmlFor={generatedId}>
                {label}
            </label>
            <div className="relative">
                <input
                    aria-autocomplete="list"
                    aria-controls={`${generatedId}-listbox`}
                    aria-expanded={isOpen}
                    autoComplete="off"
                    className={`${formStyles.input} pr-16`}
                    id={generatedId}
                    placeholder={placeholder ?? 'Type to search…'}
                    ref={inputRef}
                    role="combobox"
                    type="text"
                    value={displayValue}
                    onChange={(event) => {
                        onSearchChange(event.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                />
                {selectedValue && !isOpen && (
                    <button
                        aria-label="Clear selection"
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-0.5 text-xs text-zinc-400 hover:text-zinc-100"
                        type="button"
                        onClick={handleClear}
                    >
                        Clear
                    </button>
                )}
                {isOpen && (
                    <div
                        className="absolute z-20 mt-1 max-h-80 w-full overflow-auto rounded-md border border-zinc-700 bg-zinc-900 shadow-lg"
                        id={`${generatedId}-listbox`}
                        ref={listRef}
                        role="listbox"
                    >
                        {totalCount === 0 ? (
                            <div className="px-3 py-2 text-sm text-zinc-500">No matches</div>
                        ) : (
                            options.map((group) => {
                                const realOptions = group.options.filter((option) => option.value !== '');
                                if (realOptions.length === 0) return null;

                                return (
                                    <div className="px-1" key={`group-${group.label}`}>
                                        <div className="px-2 pt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                                            {group.label}
                                        </div>
                                        {realOptions.map((option) => {
                                            const flatItem = flatOptions.find((entry) => entry.value === option.value);
                                            const flatIndex = flatItem?.flatIndex ?? -1;
                                            const isHighlighted = flatIndex === effectiveHighlight;
                                            const isSelected = option.value === selectedValue;

                                            return (
                                                <button
                                                    aria-selected={isSelected}
                                                    className={`block w-full rounded px-2 py-1 text-left text-sm ${
                                                        isHighlighted
                                                            ? 'bg-zinc-800 text-zinc-50'
                                                            : isSelected
                                                              ? 'bg-zinc-950 text-zinc-100'
                                                              : 'text-zinc-200 hover:bg-zinc-800/60'
                                                    }`}
                                                    data-flat-index={flatIndex}
                                                    key={option.value}
                                                    role="option"
                                                    type="button"
                                                    onClick={() => {
                                                        onSelect(option.value);
                                                        onSearchChange('');
                                                        closeDropdown();
                                                    }}
                                                    onMouseEnter={() => {
                                                        if (flatIndex >= 0) setHighlightedIndex(flatIndex);
                                                    }}
                                                >
                                                    {option.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
            {helpText && <div className={formStyles.helpText}>{helpText}</div>}
        </div>
    );
};

export default FeatureTypeahead;
